// Display copy of the slot rules. api/_lib/slots.js is the authoritative one:
// the server prices every booking and re-checks every rule. Keep them in step.

export const TIME_BLOCKS = ["morning", "afternoon", "evening"];

export const TIME_SLOTS = {
  morning: [
    "7:00 AM - 7:30 AM", "7:30 AM - 8:00 AM", "8:00 AM - 8:30 AM",
    "8:30 AM - 9:00 AM", "9:00 AM - 9:30 AM", "9:30 AM - 10:00 AM",
    "10:00 AM - 10:30 AM", "10:30 AM - 11:00 AM", "11:00 AM - 11:30 AM",
    "11:30 AM - 12:00 PM",
  ],
  afternoon: [
    "12:00 PM - 12:30 PM", "12:30 PM - 1:00 PM", "1:00 PM - 1:30 PM",
    "1:30 PM - 2:00 PM", "2:00 PM - 2:30 PM", "2:30 PM - 3:00 PM",
    "3:00 PM - 3:30 PM", "3:30 PM - 4:00 PM", "4:00 PM - 4:30 PM",
    "4:30 PM - 5:00 PM",
  ],
  evening: [
    "5:00 PM - 5:30 PM", "5:30 PM - 6:00 PM", "6:00 PM - 6:30 PM",
    "6:30 PM - 7:00 PM", "7:00 PM - 7:30 PM", "7:30 PM - 8:00 PM",
    "8:00 PM - 8:30 PM", "8:30 PM - 9:00 PM", "9:00 PM - 9:30 PM",
    "9:30 PM - 10:00 PM", "10:00 PM - 10:30 PM", "10:30 PM - 11:00 PM",
    "11:00 PM - 11:30 PM", "11:30 PM - 12:00 AM",
  ],
};

export const ALL_SLOTS = [...TIME_SLOTS.morning, ...TIME_SLOTS.afternoon, ...TIME_SLOTS.evening];

// Off-peak = 9:00 AM – 5:00 PM. Rate: ₹150 per 30-min slot (₹300/hr).
// Peak   = everything else.        Rate: ₹250 per 30-min slot (₹500/hr).
export const OFF_PEAK_SLOTS = new Set([
  "9:00 AM - 9:30 AM", "9:30 AM - 10:00 AM", "10:00 AM - 10:30 AM",
  "10:30 AM - 11:00 AM", "11:00 AM - 11:30 AM", "11:30 AM - 12:00 PM",
  "12:00 PM - 12:30 PM", "12:30 PM - 1:00 PM", "1:00 PM - 1:30 PM",
  "1:30 PM - 2:00 PM", "2:00 PM - 2:30 PM", "2:30 PM - 3:00 PM",
  "3:00 PM - 3:30 PM", "3:30 PM - 4:00 PM", "4:00 PM - 4:30 PM",
  "4:30 PM - 5:00 PM",
]);

export const PEAK_RATE = 250;     // ₹ per 30-min slot
export const OFF_PEAK_RATE = 150; // ₹ per 30-min slot

export const rateFor = (slot) => (OFF_PEAK_SLOTS.has(slot) ? OFF_PEAK_RATE : PEAK_RATE);

/** "7:00 PM - 7:30 PM" -> "7:00 PM", the start time alone. */
export const slotStart = (slot) => String(slot).split(" - ")[0];

/** "7:00 PM - 7:30 PM" -> "7:30 PM". */
export const slotEnd = (slot) => String(slot).split(" - ")[1] || "";

/**
 * Chip label for a slot. "7:00 AM - 7:30 AM" reads as "7:00 – 7:30 AM": the
 * shared meridiem is stated once so the range still fits a chip. A slot that
 * crosses noon or midnight keeps both ("11:30 AM – 12:00 PM").
 */
export function slotLabel(slot) {
  const [start, end] = String(slot).split(" - ");
  if (!end) return slot;
  return start.slice(-2) === end.slice(-2)
    ? `${start.slice(0, -3)} – ${end}`
    : `${start} – ${end}`;
}

/** Minutes after midnight for a "7:30 PM" style time. */
function minutesOf(time) {
  const m = String(time).match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) return 0;
  let h = Number(m[1]) % 12;
  if (m[3].toUpperCase() === "PM") h += 12;
  return h * 60 + Number(m[2]);
}

/** YYYY-MM-DD in the visitor's own time zone (toISOString would give UTC). */
export function localISO(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** True when the slot on that date has already started. */
export function isPastSlot(dateStr, slot, now = new Date()) {
  const today = localISO(now);
  if (dateStr < today) return true;
  if (dateStr > today) return false;
  return minutesOf(slotStart(slot)) <= now.getHours() * 60 + now.getMinutes();
}

/** Which block a slot belongs to. */
export const blockOf = (slot) => TIME_BLOCKS.find((b) => TIME_SLOTS[b].includes(slot));

/** Sorted in day order, which the price summary and the time range rely on. */
export const sortSlots = (slots) =>
  [...slots].sort((a, b) => ALL_SLOTS.indexOf(a) - ALL_SLOTS.indexOf(b));

/** "Tue 27 Oct" for a YYYY-MM-DD string. */
export function prettyDate(dateStr, opts = { weekday: "short", day: "numeric", month: "short" }) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", opts);
}

/**
 * Human time range for a set of slots: "7:00 PM – 8:00 PM" when they run
 * back to back, otherwise the number of separate slots.
 */
export function timeRange(slots) {
  const sorted = sortSlots(slots);
  if (!sorted.length) return "";
  const idx = sorted.map((s) => ALL_SLOTS.indexOf(s));
  const contiguous = idx.every((v, i) => i === 0 || v === idx[i - 1] + 1);
  if (contiguous) return `${slotStart(sorted[0])} – ${slotEnd(sorted[sorted.length - 1])}`;
  return sorted.map(slotLabel).join(", ");
}

/**
 * Ten-digit Indian mobile number from whatever was typed or pasted:
 * "+91 98765 43210", "098765 43210" and "9876543210" all become 9876543210.
 */
export function cleanPhone(value) {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.length > 10 && digits.startsWith("91")) digits = digits.slice(2);
  if (digits.length > 10 && digits.startsWith("0")) digits = digits.slice(1);
  return digits.slice(0, 10);
}
